import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Steps } from '@/components/composed'
import {
  LandingAffiliation,
  LandingApplyingOfferForm,
  LandingBenefits,
  LandingConditionsTable,
  LandingHeader,
} from '@/components/composed/landingBlocks'
import { LandingAccessControlProvider } from '@/components/composed/landingBlocks/LandingAccessControlProvider'
import { financingPageBreadcrumb } from '@/components/ui/Breadcrumbs/constants'
import { getLocation } from '@/logic/app/reducer'
import { financingProduct } from '@/logic/metrika/constants'
import {
  getEventContextGtmFinancingOfferLendingShow,
  getGtmScrollEventPayloadFinancingOfferForm,
  pushGtmEventOnClickApplyOnFundingOfferPage,
  pushGtmEventOnShowLendingFinancingProducts,
} from '@/logic/metrika/financing/gtmHelpers'
import type { IBaseGtmEventFinancing } from '@/logic/metrika/financing/types'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { usePushScrollGtmEvent } from '@/logic/metrika/usePushScrollGtmEvent'
import { getUserRegion } from '@/logic/userRegion'
import type { RootState } from '@/redux/interfaces'
import type { EFinancingOfferRoute } from '@/types'
import { EFinancingOfferType, EFinancingProductId } from '@/types'
import { asyncScrollIntoView } from '@/utils/helpers'
import { useWebpExtension } from '@/utils/hooks/image'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { getStaticImagePath } from '@/utils/images'
import { benefits, details, investmentConditionsTable, steps } from './constants'
import { EScrollEventBlockId } from './gtmConstants'
import { messages } from './messages'
import './styles.scss'

const mapState = (state: RootState) => ({
  location: getLocation()(state),
  userGtmInfo: getUserGtmInfo()(state),
  userRegion: getUserRegion()(state),
})

const rootBreadcrumbs = [financingPageBreadcrumb]
const partnerImages = ['vtb']

const formId = EScrollEventBlockId.financingOfferForm

const InvestmentCredit: React.FC = () => {
  const { location, userGtmInfo, userRegion } = useSelector(mapState)
  const imageExtension = useWebpExtension()

  const { isDesktop, isMobile } = useDeviceType()

  const leavingRequestClicked = () => () => {
    asyncScrollIntoView(`#${formId}`)
    pushGtmEventOnClickApplyOnFundingOfferPage(userGtmInfo, userRegion.name, EFinancingProductId.INVESTMENT)
  }

  useEffect(() => {
    pushGtmEventOnShowLendingFinancingProducts(
      userGtmInfo,
      getEventContextGtmFinancingOfferLendingShow(location.pathname, financingProduct[location.pathname as EFinancingOfferRoute]),
    ) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  usePushScrollGtmEvent<string, IBaseGtmEventFinancing>({
    blockIds: [formId],
    gtmScrollEventPayloads: {
      [formId]: getGtmScrollEventPayloadFinancingOfferForm(userGtmInfo, userRegion.name, EFinancingProductId.INVESTMENT),
    },
  })

  const actionButtonText = messages.header.button
  const titleText = messages.header.title
  const descriptionText = messages.header.description
  const backgroundImage = getStaticImagePath(`financingOffer/investment.${imageExtension}`)

  return (
    <LandingAccessControlProvider>
      <div className="investment-credit-page">
        <LandingHeader
          isDeviceMobile={isMobile}
          actionButtonText={actionButtonText}
          titleText={titleText}
          descriptionText={descriptionText}
          styles={{
            backgroundImage: isDesktop ? `url("${backgroundImage}")` : undefined,
          }}
          partnerImages={partnerImages}
          onActionClick={leavingRequestClicked()}
          details={details}
          breadcrumbCaption={messages.breadcrumb}
          rootBreadcrumbs={rootBreadcrumbs}
          buttonTooltipMessage={messages.forAgrariansOnly}
        />
        <LandingBenefits
          bannerTitleText={messages.simpleRequest}
          bannerBackgroundImage="financingOffer/investmentBenefits"
          benefits={benefits}
          onActionClick={leavingRequestClicked()}
          blockId={EScrollEventBlockId.investmentCreditPageBenefits}
          buttonTooltipMessage={messages.forAgrariansOnly}
        />
        <LandingAffiliation
          descriptionText={messages.affiliation.description}
          shouldShowDescription
          blockId={EScrollEventBlockId.investmentCreditPageAffiliation}
        />
        <Steps
          blockId={EScrollEventBlockId.investmentCreditPageStages}
          title={messages.steps.title}
          onButtonClick={leavingRequestClicked()}
          steps={steps}
          buttonText={messages.steps.button}
          buttonTooltipMessage={messages.forAgrariansOnly}
        />
        <LandingConditionsTable
          blockId={EScrollEventBlockId.investmentCreditPageConditions}
          conditionsTable={investmentConditionsTable}
          tableTitleId={messages.conditions.title}
          shouldMarkFirstRow
        />
        <LandingApplyingOfferForm
          formId={formId}
          title={messages.form.title}
          offerType={EFinancingOfferType.CREDIT}
          productId={EFinancingProductId.INVESTMENT}
        />
      </div>
    </LandingAccessControlProvider>
  )
}

export default InvestmentCredit
